import { useState, useEffect } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, PieChart, Pie, Cell, CartesianGrid
} from "recharts";

export default function DashboardHome() {
  const [events, setEvents] = useState([]);
  const [reportDefs, setReportDefs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [eventsRes, reportsRes] = await Promise.all([
          fetch(process.env.REACT_APP_BACKEND_URL+"/button_press_data"),
          fetch(process.env.REACT_APP_BACKEND_URL+"/button_report_definitions")
        ]);

        const eventsData = await eventsRes.json();
        const reportsData = await reportsRes.json();

        setEvents(Array.isArray(eventsData) ? eventsData : []);
        setReportDefs(Array.isArray(reportsData) ? reportsData : []);
      } catch (err) {
        console.error("Failed to fetch dashboard data:", err);
        setEvents([]);
        setReportDefs([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) return <p className="p-4">Datu ielāde...</p>;
  if (!events.length) return <p className="p-4">Nav aktīvu notikumu.</p>;

  // Process data for charts
  const recentEvents = events
    .slice()
    .sort((a, b) => b.timestamp - a.timestamp)
    .slice(0, 50);

  const buttonCounts = {};
  const suppliesButtonCounts = {};
  const serviceButtonCounts = {};

  recentEvents.forEach(e => {
    const def = reportDefs.find(r => r.serial === e.serial && r.button_id === e.button_id);
    if (def.report_type === 1) { //Service type
      serviceButtonCounts[e.button_id] = (serviceButtonCounts[e.button_id] || 0) + 1;
    } else if (def.report_type === 2) { //Supplies type
      suppliesButtonCounts[e.button_id] = (suppliesButtonCounts[e.button_id] || 0) + 1;
    }
    buttonCounts[e.button_id] = (buttonCounts[e.button_id] || 0) + 1;
  });

  const buttonData = Object.keys(buttonCounts).map(k => ({ button: `Button ${k}`, count: buttonCounts[k] }));
  const suppliesButtonData = Object.keys(suppliesButtonCounts).map(k => ({ button: `Button ${k}`, count: suppliesButtonCounts[k] }));
  const serviceButtonData = Object.keys(serviceButtonCounts).map(k => ({ button: `Button ${k}`, count: serviceButtonCounts[k] }));
  
  const reportCounts = {};
  recentEvents.forEach(e => {
    const def = reportDefs.find(r => r.serial === e.serial && r.button_id === e.button_id);
    const reportText = def ? def.report_text : "No Report";
    reportCounts[reportText] = (reportCounts[reportText] || 0) + 1;
  });
  const reportData = Object.keys(reportCounts).map(k => ({ name: k, value: reportCounts[k] }));
  const COLORS = ["#8884d8", "#82ca9d", "#ffc658", "#ff7f50", "#a4de6c", "#d0ed57"];

  return (
    <div className="p-4">
      <h2 className="text-2xl font-bold mb-4">Pārskats</h2>

      {/* Unprocessed Events */}
      <div className="mb-4 p-4 bg-red-100 rounded">
        {recentEvents.filter(e => !e.processed).length > 0
          ? <p>{recentEvents.filter(e => !e.processed).length} neapstrādāti notikumi</p>
          : <p>Visi notikumi ir apstrādāti ✅</p>}
      </div>

      {/* Charts */}
      <div className="flex flex-wrap gap-6 mb-6">

        <div className="border rounded shadow p-4 w-full md:w-1/3">
          <h3 className="font-semibold mb-2">Novērtējumu īpatsvars</h3>
          {serviceButtonData.length ? (
            <BarChart width={350} height={250} data={serviceButtonData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="button" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill="#8884d8" />
            </BarChart>
          ) : (
            <p>Nav reģistrētu notikumu</p>
          )}
        </div>

        <div className="border rounded shadow p-4 w-full md:w-1/3">
          <h3 className="font-semibold mb-2">Pasūtījumu īpatsvars</h3>
          {suppliesButtonData.length ? (
            <BarChart width={350} height={250} data={suppliesButtonData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="button" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill="#8884d8" />
            </BarChart>
          ) : (
            <p>Nav reģistrētu notikumu</p>
          )}
        </div>

        <div className="border rounded shadow p-4 w-full md:w-1/2">
          <h3 className="font-semibold mb-2">Notikumu īpatsvars</h3>
          {reportData.length ? (
            <PieChart width={400} height={250}>
              <Pie data={reportData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                {reportData.map((entry, index) => (
                  <Cell key={index} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          ) : (
            <p>Nav reģistrētu notikumu</p>
          )}
        </div>
      </div>
    </div>
  );
}
